/* @flow */
import { cloneDeep } from 'lodash';
import Entity from 'lib/Entity';
import { SCHEMA_VERSION_SUPPORTED,
        TriggerTypes,
        PromotionTypes,
        ImageTypes,
        ButtonActionTypes } from 'constants/QuickPromotionConstants';
import QuickPromotionAction from 'entities/QuickPromotionAction';
import apiRequest from 'lib/apiRequest';

class QuickPromotion extends Entity {
  initialize() {
    this.url = '/quick_promotions';
  }

  getPromotion(): string {
    return this.get('promotion');
  }

  getSchemaVersion(): number {
    return this.get('schema_version');
  }

  getEndDate(): ?Date {
    return this.get('end_date');
  }

  getType(): number {
    return this.get('type');
  }

  getTrigger(): number {
    return this.get('trigger');
  }

  getHeader(): ?string {
    return this.get('header');
  }

  getBody(): ?string {
    return this.get('body');
  }

  getImageUrl(): ?string {
    return this.get('image_url');
  }

  getImageType(): number {
    return this.get('image_type');
  }

  getPrimaryAction(): QuickPromotionAction {
    return this.get('primary_action');
  }

  getSecondaryAction(): ?QuickPromotionAction {
    return this.get('secondary_action');
  }

  parse(response: Object) {
    const promotion = cloneDeep(response);
    promotion['primary_action'] = QuickPromotionAction.fromJSON(promotion['primary_action']);
    if (promotion['secondary_action']) {
      promotion['secondary_action'] = QuickPromotionAction.fromJSON(promotion['secondary_action']);
    }
    if (promotion['end_date']) {
      promotion['end_date'] = new Date(promotion['end_date']);
    }
    promotion['trigger'] = TriggerTypes[promotion['trigger'].toUpperCase()]
                        || TriggerTypes.UNKNOWN;
    promotion['type'] = PromotionTypes[promotion['type'].toUpperCase()]
                     || PromotionTypes.UNKNOWN;
    promotion['image_type'] = ImageTypes[promotion['image_type'].toUpperCase()]
                           || ImageTypes.UNKNOWN;
    return promotion;
  }

  // Whether we understand how to show this QP
  isShowable(): boolean {
    // If it's an old schema, don't show it
    if (this.getSchemaVersion() > SCHEMA_VERSION_SUPPORTED) {
      return false;
    }

    // If the QPs already expired, don't show it
    const endDate = this.getEndDate();
    if (endDate != null && new Date() > endDate) {
      return false;
    }

    // If we don't know what type of QP it is, don't show it
    if (this.getType() === PromotionTypes.UNKNOWN) {
      return false;
    }

    // If we don't know what type of QP it is, don't show it
    if (this.getTrigger() === TriggerTypes.UNKNOWN) {
      return false;
    }

    // If we don't know how to display its image, don't show it
    if (this.getImageType() === ImageTypes.UNKNOWN) {
      return false;
    }

    // If we don't know how to handle one of the requested action types, don't show it
    if (this.getPrimaryAction() != null
        && this.getPrimaryAction().getActionType() === ButtonActionTypes.UNKNOWN) {
      return false;
    }

    const secondaryAction = this.getSecondaryAction();
    if (secondaryAction != null
        && secondaryAction.getActionType() === ButtonActionTypes.UNKNOWN) {
      return false;
    }

    // all checks passed, we should be able to show it!
    return true;
  }

  recordDismissal(response: string) {
    return apiRequest(`${this.url}`, 'POST', {
      promotion: this.getPromotion(),
      response,
    });
  }
}

export default QuickPromotion;



// WEBPACK FOOTER //
// ./src/entities/QuickPromotion.js